import React, { useState, useRef, useEffect } from 'react';
import { 
  Card, 
  Select, 
  Upload, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Space, 
  Divider,
  message,
  Row,
  Col,
  Modal,
  Spin,
  Radio
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { GMCC_ASSET_VERSION } from './consts';
import "./FlyerRatesSetup.css";
import { Document, Page, pdfjs } from 'react-pdf';
import { FLYER_RATE_SETUP_URL_MAP, FLYER_RATE_SETUP_NAMES_MAP } from './consts';

const { Title, Text } = Typography;
const { Option } = Select;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FlyerRatesSetup = () => {
  const [selectedProgram, setSelectedProgram] = useState("jumbocra");
  const [previewURL, setPreviewURL] = useState(null);
  const [storingImageData, setStoringImageData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [previewImage, setPreviewImage] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [setupDataMap, setSetupDataMap] = useState({});

  const canvasRef = useRef(null);

  const formInitials = {
    program: "jumbocra",
    rate: "",
    apr: "",
    term: "",
    loanAmount: "",
    ltv: "",
    purpose: "",
    occupancy: "",
    fico: "",
    propertyType: "",
    dti: "",
    origination: "",
    armIndex: "",
    armMargin: "",
    armCaps: "",
    notes: "",
    regionScope: "allregions",
    targetRegion: "",
    otherRate: "",
    otherApr: "",
    otherTerm: "",
  }

  const getAllRateSetupConfig = () => {
    fetch('/los/gmcc/getallratesetupconfig')
    .then(response => response.json())
    .then(data => {
        setSetupDataMap(data);
        if (data[selectedProgram]) {
          form.setFieldsValue(data[selectedProgram]);
        }
      console.log('Success:', data);
    })
    .catch(error => {
      console.error('Failed to fetch rate setup config:', error);
    });
  }

  useEffect(() => {
    getAllRateSetupConfig();
  }, []);


  const handleProgramChange = (value) => {
    setSelectedProgram(value);
    // form.resetFields();
    setPreviewImage(null);
    setFileList([]);
    if (setupDataMap[value]) {
      form.setFieldsValue(setupDataMap[value]);
    } else {
      form.setFieldsValue(formInitials);
      form.setFieldValue("program", value);
    }
  };

  const handleUploadChange = ({ fileList }) => {
    setFileList(fileList);
  };

  function dataURLtoBlob(dataURL) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
 }

  const handleFormSubmit = (values) => {
    console.log('Form values:', values);
    setIsLoading(true);


    const formData = new FormData();
    if (selectedProgram.includes("ratesheet")) {
        formData.append('type', "ratesheet");
        formData.append('regularFileData', fileList[0].originFileObj);
        formData.append('templateFileData', "");
        FLYER_RATE_SETUP_NAMES_MAP[selectedProgram].regular.forEach(name => {
            formData.append('regularNames[]', name);
        })
        FLYER_RATE_SETUP_NAMES_MAP[selectedProgram].letterSize.forEach(name => {
            formData.append('letterSizeNames[]', name);
        })
    } else {
        formData.append('type', "flyer");
        const regularBlob = dataURLtoBlob(storingImageData.regular);
        const templateBlob = dataURLtoBlob(storingImageData.template);
        const regularFile = new File([regularBlob], "regular.png", { type: "image/png" });
        const templateFile = new File([templateBlob], "template.png", { type: "image/png" });   
        formData.append('regularFileData', regularFile);
        formData.append('templateFileData', templateFile);
        FLYER_RATE_SETUP_NAMES_MAP[selectedProgram].regular.forEach(name => {
            formData.append('regularNames[]', name);
        })
        FLYER_RATE_SETUP_NAMES_MAP[selectedProgram].letterSize.forEach(name => {
            formData.append('letterSizeNames[]', name);
        })
    }
    formData.append("program_name", selectedProgram);
    formData.append("setup_config", JSON.stringify(values));
    fetch('/los/gmcc/updateflyersetup', {
      method: 'POST',
      body: formData,
    })
    .then(response => response.json())
    .then(data => {
      console.log('Success:', data);
      message.success('Setup saved successfully');
      getAllRateSetupConfig();
      setIsLoading(false);
    })
    .catch((error) => {
      console.error('Error:', error);
      message.error('Error saving the setup, please try again later or contact andrew.zhang@zeitro.com');
      setIsLoading(false);
    });


  };

  const onFlyerPreview = () => {
    const rateText = form.getFieldValue('rate');
    const aprText = form.getFieldValue('apr');
    const termText = form.getFieldValue('term');
    const loanAmountText = form.getFieldValue('loanAmount');
    const ltvText = form.getFieldValue('ltv');
    const purposeText = form.getFieldValue('purpose');
    const occupancyText = form.getFieldValue('occupancy');
    const ficoText = form.getFieldValue('fico');
    const propertyTypeText = form.getFieldValue('propertyType');
    const dtiText = form.getFieldValue('dti');
    const originationText = form.getFieldValue('origination');
    const armIndexText = form.getFieldValue('armIndex');
    const armMarginText = form.getFieldValue('armMargin');
    const armCapsText = form.getFieldValue('armCaps');
    const notesText = form.getFieldValue('notes');
    const regionScopeText = form.getFieldValue('regionScope');
    const targetRegionText = form.getFieldValue('targetRegion');
    const otherRateText = form.getFieldValue('otherRate');
    const otherAprText = form.getFieldValue('otherApr');
    const otherTermText = form.getFieldValue('otherTerm');

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Create two images
    const image1 = new Image();
    const image2 = new Image();
    image1.crossOrigin = "anonymous";
    image2.crossOrigin = "anonymous";
    
    // Set sources
    image1.src = FLYER_RATE_SETUP_URL_MAP[selectedProgram];
    image2.src = FLYER_RATE_SETUP_URL_MAP["template_" + selectedProgram];

    // Track when both images are loaded
    let loadedImages = 0;
    const totalImages = 2;
    
    const processImages = () => {
        loadedImages++;
        if (loadedImages === totalImages) {
            // Both images loaded, now we can draw them
            canvas.width = image1.width;
            canvas.height = image1.height;
            
            // Draw first image
            ctx.drawImage(image1, 0, 0);
            
            // Get text measurements and draw text on first image
            const rateTextWidth = ctx.measureText(rateText).width;
            const aprTextWidth = ctx.measureText(aprText).width;
            const termTextWidth = ctx.measureText(termText).width;
            const positionOffsetY_program = selectedProgram === '40down' || selectedProgram === 'fabulous' ? 80 : 0;
            
            // Draw text on first image
            ctx.textAlign = 'center';
            ctx.font = '600 32px Inter';
            ctx.fillStyle = 'black';
            const termLine = termText
            const rateLine = rateText + "%" + " (APR " + aprText + "%)"
            if (selectedProgram === 'hermes') {
                ctx.fillText(termLine, 248, 216 + positionOffsetY_program);
            } else {
                ctx.fillText(termLine, 248, 276 + positionOffsetY_program);
            }
            ctx.fillStyle = "#F20B0B";
            if (selectedProgram === 'hermes') {
                ctx.fillText(rateLine, 248, 256 + positionOffsetY_program);
            } else {
                ctx.fillText(rateLine, 248, 316 + positionOffsetY_program);
            }

            // asof date format: 04/20/2025
            const asofDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
            const asofLine = "Rate as of " + asofDate;
            ctx.fillStyle = 'black';
            ctx.font = '600 11px Inter';
            if (selectedProgram === 'hermes') {
                ctx.fillText(asofLine, 248, 296 + positionOffsetY_program);
            } else {
                ctx.fillText(asofLine, 248, 356 + positionOffsetY_program);
            }
            if (selectedProgram === '40down' || selectedProgram === 'fabulous') {
                ctx.font = '400 9px Inter';
            } else {
                ctx.font = '400 11px Inter';
            }
            const loanAmount = loanAmountText;
            const loanAmountShort = loanAmount.length === 7 ? parseFloat(loanAmount) / 1000000 : parseFloat(loanAmount) / 1000;
            const loanAmountPrefix = loanAmount.length === 7 ? "M" : "K";
            const firstLine = "Loan amount: $" + loanAmountShort + loanAmountPrefix + ", LTV: " + ltvText + "%, FICO: " + ficoText + ", DTI: " + dtiText + "%, Origination: " + originationText + "%."
            const secondLine = "Purpose: " + purposeText + ", Occupancy: " + occupancyText + ", Property type: " + propertyTypeText + "."
            const thirdLine = "ARM Index: " + armIndexText + ", Margin: " + armMarginText + ", Caps: " + armCapsText + "."
            const noteLine = notesText;
            if (selectedProgram === 'hermes') {
                ctx.fillText(firstLine, 248, 316 + positionOffsetY_program);
                ctx.fillText(secondLine, 248, 336 + positionOffsetY_program);
            } else {
                ctx.fillText(firstLine, 248, 376 + positionOffsetY_program);
                ctx.fillText(secondLine, 248, 396 + positionOffsetY_program);
            }
            if (armIndexText && armMarginText && armCapsText) {
                if (selectedProgram === 'hermes') {
                    ctx.fillText(thirdLine, 248, 356 + positionOffsetY_program);
                } else {
                    ctx.fillText(thirdLine, 248, 416 + positionOffsetY_program);
                }
                if (noteLine) {
                    if (noteLine.length > 50) {
                        noteLine.split(',').forEach((line, index) => {
                            if (selectedProgram == 'hermes') {
                                ctx.fillText(line, 248, 376 + positionOffsetY_program + index * 20);
                            } else {
                                ctx.fillText(line, 248, 436 + positionOffsetY_program + index * 20);
                            }
                        });
                    } else {
                        if (selectedProgram == 'hermes') {
                            ctx.fillText(noteLine, 248, 376 + positionOffsetY_program);
                        } else {
                            ctx.fillText(noteLine, 248, 436 + positionOffsetY_program);
                        }
                    }
                }
            } else {
                if (noteLine) {
                    if (noteLine.length > 50) { 
                        noteLine.split(',').forEach((line, index) => {
                            if (selectedProgram == 'hermes') {
                                ctx.fillText(line, 248, 376 + positionOffsetY_program + index * 20);
                            } else {
                                ctx.fillText(line, 248, 416 + positionOffsetY_program + index * 20);
                            }
                        });
                    } else {
                        if (selectedProgram == 'hermes') {
                            ctx.fillText(noteLine, 248, 376 + positionOffsetY_program);
                        } else {
                            ctx.fillText(noteLine, 248, 416 + positionOffsetY_program);
                        }
                    }
                }
            }

            // Store the first image data for preview
            const imageURL1 = canvas.toDataURL('image/png');
            setPreviewURL(imageURL1); // This will show in the preview
            
            // Clear canvas and draw second image
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(image2, 0, 0);
            
            // Draw text on second image with template offset
            const positionOffsetY_template = selectedProgram === '40down'? 200: 170;
            ctx.textAlign = 'center';
            ctx.font = '600 32px Inter';
            ctx.fillStyle = 'black';
            if (selectedProgram === 'hermes') {
                ctx.fillText(termLine, 248, 216 + positionOffsetY_template);
            } else {
                ctx.fillText(termLine, 248, 276 + positionOffsetY_template);
            }
            ctx.fillStyle = "#F20B0B";
            if (selectedProgram === 'hermes') {
                ctx.fillText(rateLine, 248, 256 + positionOffsetY_template);
            } else {
                ctx.fillText(rateLine, 248, 316 + positionOffsetY_template);
            }

            // asof date format: 04/20/2025
            ctx.fillStyle = 'black';
            ctx.font = '600 11px Inter';
            if (selectedProgram === 'hermes') {
                ctx.fillText(asofLine, 248, 296 + positionOffsetY_template);
            } else {
                ctx.fillText(asofLine, 248, 356 + positionOffsetY_template);
            }
            if (selectedProgram === '40down' || selectedProgram === 'fabulous') {
                ctx.font = '400 9px Inter';
            } else {
                ctx.font = '400 11px Inter';
            }
            if (selectedProgram === 'hermes') {
                ctx.fillText(firstLine, 248, 316 + positionOffsetY_template);
                ctx.fillText(secondLine, 248, 336 + positionOffsetY_template);
            } else {
                ctx.fillText(firstLine, 248, 376 + positionOffsetY_template);
                ctx.fillText(secondLine, 248, 396 + positionOffsetY_template);
            }
            if (armIndexText && armMarginText && armCapsText) {
                if (selectedProgram === 'hermes') {
                    ctx.fillText(thirdLine, 248, 356 + positionOffsetY_template);
                } else {
                    ctx.fillText(thirdLine, 248, 416 + positionOffsetY_template);
                }
                if (noteLine) {
                    if (noteLine.length > 50) {
                        noteLine.split(',').forEach((line, index) => {
                            if (selectedProgram === 'hermes') {
                                ctx.fillText(line, 248, 376 + positionOffsetY_template + index * 20);
                            } else {
                                ctx.fillText(line, 248, 436 + positionOffsetY_template + index * 20);
                            }
                        });
                    } else {
                        if (selectedProgram === 'hermes') {
                            ctx.fillText(noteLine, 248, 376 + positionOffsetY_template);
                        } else {
                            ctx.fillText(noteLine, 248, 436 + positionOffsetY_template);
                        }
                    }
                }
            } else {
                if (noteLine) {
                    if (noteLine.length > 50) { 
                        noteLine.split(',').forEach((line, index) => {
                            if (selectedProgram === 'hermes') {
                                ctx.fillText(line, 248, 376 + positionOffsetY_template + index * 20);
                            } else {
                                ctx.fillText(line, 248, 416 + positionOffsetY_template + index * 20);
                            }
                        });
                    } else {
                        if (selectedProgram === 'hermes') {
                            ctx.fillText(noteLine, 248, 376 + positionOffsetY_template);
                        } else {
                            ctx.fillText(noteLine, 248, 416 + positionOffsetY_template);
                        }
                    }
                }
            }

            // Store the second image data
            const imageURL2 = canvas.toDataURL('image/png');
            
            // Store both image data for later backend submission
            const imageData = {
                regular: imageURL1,
                template: imageURL2
            };
            
            setStoringImageData(imageData);
            
            // You can store this imageData in state or pass it to a parent component
            // setImageData(imageData); // If you have a state for this
        }
    };
    
    // Set up load handlers for both images
    image1.onload = processImages;
    image2.onload = processImages;

    setPreviewModalOpen(true);
  };

  const renderUploadSection = () => {
    if (!selectedProgram.includes("ratesheet")) return null;

    return (  
        <>
            <Form.Item 
                label="Update Rate Sheet" 
                name="pdfFile"
        >
            <Upload
            accept=".pdf"
            maxCount={1}
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={() => false}
            >
            <Button icon={<UploadOutlined />}>Select PDF</Button>
            </Upload>
        </Form.Item>
        <Row gutter={[16, 16]} span={24}>
            <Col span={4} className='flyerRatesSetupButtonWrap'>
                <Form.Item>
                    <Button type="primary" htmlType="submit" style={{width: '150px', height: '40px'}}>
                        {isLoading ? <Spin /> : "Save"}
                    </Button>
                </Form.Item>
            </Col>
        </Row>
        </>
    );
  };

  const renderSampleRateSection = () => {
    if (selectedProgram?.includes("ratesheet")) return null;

    return (
      <>
        <div className='flyerRatesSetupTitle'>Sample Rate & APR</div>
        <Row gutter={[16, 16]} span={24}>
            <Col span={8}>
                <Form.Item 
                    label="Today's Rate" 
                    name="rate"
                    // rules={[{ required: true, message: 'Please enter today\'s rate' }]}
                >
                    <Input placeholder="eg. 6.000"  suffix="%"/>
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item 
                    label="APR" 
                    name="apr"
                    // rules={[{ required: true, message: 'Please enter APR' }]}
                >
                    <Input placeholder="eg. 7.000" suffix="%"/>
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item 
                    label="Loan term" 
                    name="term"
                    // rules={[{ required: true, message: 'Please enter term' }]}
                >
                    <Input placeholder="eg. 30 year fixed, 7/6 ARM" suffix=" " />
                </Form.Item>
            </Col>
        </Row>
        <div className='flyerRatesSetupTitle'>Scenario Terms</div>
        <Row gutter={[16, 16]} span={24}>
            <Col span={6}>
                <Form.Item 
                    label="Loan amount" 
                    name="loanAmount"
                    // rules={[{ required: true, message: 'Please enter loan amount' }]}
                >
                    <Input placeholder="eg. 1,000,000"  prefix="$"/>
                </Form.Item> 
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="LTV" 
                    name="ltv"
                    // rules={[{ required: true, message: 'Please enter LTV' }]}
                >
                    <Input placeholder="eg. 80%" suffix="%" />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Loan purpose" 
                    name="purpose"
                    // rules={[{ required: true, message: 'Please enter purpose' }]}
                >
                    <Input placeholder="eg. Purchase, Refinance, Cash Out"  suffix=" "/>
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Occupancy" 
                    name="occupancy"
                    // rules={[{ required: true, message: 'Please enter occupancy' }]}
                >
                    <Input placeholder="eg. Primary, Second Home, Investment"  suffix=" "/>
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="FICO score" 
                    name="fico"
                    // rules={[{ required: true, message: 'Please enter FICO score' }]}
                >
                    <Input placeholder="eg. 720"  suffix=" "/>
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Property type" 
                    name="propertyType"
                    // rules={[{ required: true, message: 'Please enter property type' }]}
                >
                    <Input placeholder="eg. SFR, 1-4 unit, Condo" suffix=" " />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="DTI" 
                    name="dti"
                    // rules={[{ required: true, message: 'Please enter DTI' }]}
                >
                    <Input placeholder="eg. 45%" suffix="%" />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Origination fee" 
                    name="origination"
                    // rules={[{ required: true, message: 'Please enter origination fee' }]}
                >
                    <Input placeholder="eg. 0.5" suffix="pt." />
                </Form.Item>
            </Col>
        </Row>
        <div className='flyerRatesSetupTitle'>ARM Terms (if applicable)</div>
        <Row gutter={[16, 16]} span={24}>
            <Col span={6}>
                <Form.Item 
                    label="Index" 
                    name="armIndex"
                >
                    <Input placeholder="eg. 30-day SOFR" suffix=" " />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Margin" 
                    name="armMargin"
                >
                    <Input placeholder="eg. 2.75" suffix="pt." />
                </Form.Item>
            </Col>
            <Col span={6}>
                <Form.Item 
                    label="Caps" 
                    name="armCaps"
                >
                    <Input placeholder="eg. 2,1,5" suffix=" " />
                </Form.Item>
            </Col>
        </Row>
        <Row gutter={[16, 16]} span={24}>

        </Row>
        <div className='flyerRatesSetupTitle'>Additional Notes</div>
        <Row gutter={[16, 16]} span={24}>
            <Col span={24}>
                <Form.Item 
                    label="Notes (max 80 characters)"
                    name="notes"
                >
                    <Input placeholder="Enter notes" maxLength={100}  suffix=" "/>
                </Form.Item>
            </Col>
        </Row>
        {/* <div className='flyerRatesSetupTitle'>More Options</div>
        <Row gutter={[16, 16]} span={24}>
            <Col span={12}>
                <Form.Item
                    label="Above setup is for all regions?"
                    name="regionScope"
                >
                    <Radio.Group>
                        <Radio value="allregions">Yes</Radio>
                        <Radio value="targetregion">No</Radio>
                    </Radio.Group>
                </Form.Item>
            </Col>
        </Row>
        <Form.Item shouldUpdate={(prev, curr) => prev.regionScope !== curr.regionScope}>
            {() =>
                form.getFieldValue("regionScope") === "targetregion" && (
                <>
                    <Row gutter={[16, 16]} span={24}>
                    <Col span={12}>
                        <Form.Item
                        label="What's the target region for above setup?"
                        name="targetRegion"
                        >
                        <Input placeholder="eg. Santa Clara, CA" suffix=" " />
                        </Form.Item>
                    </Col>
                    </Row>
                    <Row gutter={[16, 16]} span={24}>
                    <Col span={8}>
                        <Form.Item
                        label="Rate for other regions"
                        name="otherRate"
                        >
                        <Input placeholder="eg. 6.000" suffix="%"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                        label="APR for other regions"
                        name="otherApr"
                        >
                        <Input placeholder="eg. 7.000" suffix="%"/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                        label="Term for other regions"
                        name="otherTerm"
                        >
                        <Input placeholder="eg. 30 year fixed, 7/6 ARM" suffix=" " />
                        </Form.Item>
                    </Col>
                    </Row>
                </>
                )
            }
        </Form.Item> */}
        <Row gutter={[16, 16]} span={24}>
            <Col span={4} className='flyerRatesSetupButtonWrap'>
                <Form.Item>
                    <Button type="primary" style={{width: '150px', height: '40px', marginRight: '15px'}} onClick={onFlyerPreview}>
                    Apply & Preview
                </Button>
                </Form.Item>
            </Col>
            <Col span={4} className='flyerRatesSetupButtonWrap'>
                <Form.Item>
                    <Button type="primary" htmlType="submit" style={{width: '150px', height: '40px'}}>
                        {isLoading ? <Spin /> : "Save"}
                    </Button>
                </Form.Item>
            </Col>
        </Row>
      </>
    );
  };

  return (
    <div className='flyerRatesSetupWrap'>
      <Form
        form={form}
        initialValues={formInitials}
        layout="vertical"
        onFinish={handleFormSubmit}
        className='flyerRatesSetupForm'
      >
        <Row
            className='flyerRatesSetupSelect flex flex-row align-items-center justify-content-around'
            gutter={[16, 16]}
            span={24}
        >
            <Col span={24}>
                <Form.Item 
                    label="Select Program" 
                    name="program"
                    rules={[{ required: true, message: 'Please select a program' }]}
                >
                    <Select 
                        placeholder="Select a program" 
                        value={selectedProgram}
                        onChange={handleProgramChange}
                        style={{ width: '100%' }}
                    >
                        <Select.OptGroup label="Rate Sheet Update">
                        <Option value="ratesheet_universe">GMCC Universe</Option>
                        <Option value="ratesheet_radiant">GMCC Radiant</Option>
                        <Option value="ratesheet_hermes">GMCC Hermes</Option>
                        <Option value="ratesheet_ocean">GMCC Ocean</Option>
                        <Option value="ratesheet_outreach">GMCC Outreach</Option>
                        </Select.OptGroup>
                        <Select.OptGroup label="Flyer Rates Update">
                        <Option value="jumbocra">GMCC - Jumbo CRA</Option>
                        <Option value="ocean">GMCC - Ocean</Option>
                        <Option value="fabulous">GMCC - Fabulous</Option>
                        <Option value="amazing">GMCC - Amazing</Option>
                        <Option value="40down">GMCC - Universe</Option>
                        <Option value="thunder">GMCC - Thunder</Option>
                        <Option value="hermes">GMCC - Hermes</Option>
                        <Option value="radiant">GMCC - Radiant</Option>
                        </Select.OptGroup>
                    </Select>
                </Form.Item>
            </Col>
        </Row>

        <Divider />
       

        {renderUploadSection()}
        {renderSampleRateSection()}

        <Modal
        className='flyerPreviewModal'
        rootClassName='flyerPreviewModalRoot'
        open={previewModalOpen}
        onCancel={()=> setPreviewModalOpen(false)}
        title="Flyer Preview"
        footer={null}
      >
        <div className='d-flex justify-content-center w-100'>
            <img 
              src={previewURL} 
              alt="Flyer Preview" 
              className='w-100'
            />
        </div>
      </Modal>
      <canvas style={{display: 'none'}} ref={canvasRef} ></canvas>

        {previewImage && (
          <div style={{ marginTop: 20 }}>
            <Title level={4}>Flyer Preview</Title>
            <img 
              src={previewImage} 
              alt="Flyer Preview" 
              style={{ maxWidth: '100%', border: '1px solid #d9d9d9' }} 
            />
          </div>
        )}



      </Form>        
    </div>

  );
};

export default FlyerRatesSetup;